import { useCallback,useEffect,useMemo,useState } from "react";
import {
  Box,Typography,CircularProgress,FormControl,Select,MenuItem,
  Tabs,Tab,Divider
} from "@mui/material";
import { useNavigate,useParams } from "react-router-dom";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const getRows=(data)=>{
  if(Array.isArray(data)) return data;
  if(Array.isArray(data.results)) return data.results;
  return [];
};

const getMatches=(data)=>{
  if(Array.isArray(data)) return data;
  if(Array.isArray(data.matches)) return data.matches;
  return [];
};

const getCategorySlug=(item)=>{
  return item?.category?.slug||item?.category_slug||item?.slug||"";
};

const getCategoryName=(item)=>{
  return item?.category?.name||item?.category_name||item?.name||"Categoría";
};

const getTeamName=(team)=>{
  return team?.name||team?.team_name||team?.team?.name||"Por definir";
};

const getTeamLogo=(team)=>{
  return team?.logo||team?.team_logo||team?.team?.logo||null;
};

const getVenueName=(match)=>{
  return match?.venue?.name||match?.venue_name||"Cancha por definir";
};

const formatTime=(value)=>{
  if(!value) return "Hora por definir";
  return String(value).slice(0,5);
};

const formatDate=(value)=>{
  if(!value) return "Fecha por definir";

  const parts=String(value).split("-");
  if(parts.length!==3) return value;

  const date=new Date(
    Number(parts[0]),
    Number(parts[1])-1,
    Number(parts[2])
  );

  const formatted=new Intl.DateTimeFormat("es-EC",{
    weekday:"short",
    day:"2-digit",
    month:"short"
  }).format(date);

  return formatted.replace(/\./g,"");
};

const formatPhase=(value)=>{
  if(value==="LEAGUE") return "Todos contra todos";
  if(value==="GROUPS") return "Fase de grupos";
  if(value==="KNOCKOUT") return "Eliminación directa";
  return value||"";
};

const getMatchResult=(match)=>{
  if(match.status==="FINISHED"){
    return {
      finished:true,
      local:match.local_score??match.score_local??0,
      visitor:match.visitor_score??match.score_visitor??0
    };
  }

  return {
    finished:false,
    local:null,
    visitor:null
  };
};

export default function AdminMatches(){
  const {tournamentSlug,categorySlug}=useParams();
  const navigate=useNavigate();
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const [loading,setLoading]=useState(true);
  const [tournaments,setTournaments]=useState([]);
  const [categories,setCategories]=useState([]);
  const [phases,setPhases]=useState([]);
  const [fixtures,setFixtures]=useState([]);
  const [selectedTournament,setSelectedTournament]=useState(tournamentSlug||"");
  const [selectedCategory,setSelectedCategory]=useState(categorySlug||"");

  const loadTournaments=useCallback(async()=>{
    try{
      const response=await fetch("/tournaments/",{
        credentials:"include"
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudieron cargar los torneos."
        );
      }

      const rows=getRows(data)
        .filter(item=>item.active!==false)
        .sort((a,b)=>{
          const season=String(b.season||"").localeCompare(
            String(a.season||""),
            "es",
            {numeric:true}
          );

          if(season!==0) return season;

          return String(a.name||"").localeCompare(
            String(b.name||""),
            "es"
          );
        });

      setTournaments(rows);

      if(tournamentSlug){
        setSelectedTournament(tournamentSlug);
      }else if(rows.length>0){
        setSelectedTournament(rows[0].slug);
      }
    }catch(error){
      setTournaments([]);
      showError(
        error.message||
        "No se pudieron cargar los torneos."
      );
    }
  },[showError,tournamentSlug]);

  const loadCategories=useCallback(async(slug)=>{
    if(!slug){
      setCategories([]);
      setSelectedCategory("");
      return;
    }

    try{
      const response=await fetch(
        `/tournaments/${slug}/categories/`,
        {credentials:"include"}
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudieron cargar las categorías."
        );
      }

      const rows=getRows(data)
        .filter(item=>item.active!==false)
        .sort((a,b)=>
          getCategoryName(a).localeCompare(
            getCategoryName(b),
            "es"
          )
        );

      setCategories(rows);

      if(rows.length===0){
        setSelectedCategory("");
        return;
      }

      const requested=
        slug===tournamentSlug
          ?categorySlug
          :"";

      const exists=
        requested&&
        rows.some(
          item=>getCategorySlug(item)===requested
        );

      setSelectedCategory(
        exists
          ?requested
          :getCategorySlug(rows[0])
      );
    }catch(error){
      setCategories([]);
      setSelectedCategory("");
      showError(
        error.message||
        "No se pudieron cargar las categorías."
      );
    }
  },[
    tournamentSlug,
    categorySlug,
    showError
  ]);

  const loadCalendar=useCallback(async(tSlug,cSlug)=>{
    if(!tSlug||!cSlug){
      setPhases([]);
      setFixtures([]);
      return;
    }

    try{
      const phasesResponse=await fetch(
        `/tournaments/${tSlug}/categories/${cSlug}/phases/`,
        {credentials:"include"}
      );

      const phasesData=await readResponse(phasesResponse);

      if(!phasesResponse.ok){
        throw new Error(
          phasesData.error||
          phasesData.detail||
          "No se pudieron cargar las fases."
        );
      }

      const phaseRows=getRows(phasesData)
        .filter(item=>item.active!==false)
        .sort(
          (a,b)=>(a.order??0)-(b.order??0)
        );

      setPhases(phaseRows);

      if(phaseRows.length===0){
        setFixtures([]);
        return;
      }

      const loaded=await Promise.all(
        phaseRows.map(async phase=>{
          const response=await fetch(
            `/matches/tournaments/${tSlug}/categories/${cSlug}/phases/${phase.slug}/fixture/`,
            {credentials:"include"}
          );

          const data=await readResponse(response);

          if(!response.ok){
            throw new Error(
              data.error||
              data.detail||
              `No se pudo cargar el calendario de ${phase.name}.`
            );
          }

          return {
            phase,
            matches:getMatches(data)
          };
        })
      );

      setFixtures(loaded);
    }catch(error){
      setPhases([]);
      setFixtures([]);
      showError(
        error.message||
        "No se pudo cargar el calendario de partidos."
      );
    }
  },[showError]);

  useEffect(()=>{
    const initialize=async()=>{
      setLoading(true);

      await loadTournaments();

      setLoading(false);
    };

    initialize();
  },[loadTournaments]);

  useEffect(()=>{
    if(!selectedTournament){
      setCategories([]);
      setSelectedCategory("");
      return;
    }

    const execute=async()=>{
      setLoading(true);

      await loadCategories(selectedTournament);

      setLoading(false);
    };

    execute();
  },[
    selectedTournament,
    loadCategories
  ]);

  useEffect(()=>{
    if(!selectedTournament||!selectedCategory){
      setPhases([]);
      setFixtures([]);
      return;
    }

    const execute=async()=>{
      setLoading(true);

      await loadCalendar(
        selectedTournament,
        selectedCategory
      );

      setLoading(false);
    };

    execute();
  },[
    selectedTournament,
    selectedCategory,
    loadCalendar
  ]);

  useEffect(()=>{
    if(!selectedTournament||!selectedCategory) return;

    const expected=
      `/admin/matches/${selectedTournament}/${selectedCategory}`;

    if(
      window.location.pathname!==expected
    ){
      navigate(expected,{replace:true});
    }
  },[
    selectedTournament,
    selectedCategory,
    navigate
  ]);

  const currentCategory=useMemo(()=>{
    return categories.find(
      item=>
        getCategorySlug(item)===
        selectedCategory
    )||null;
  },[
    categories,
    selectedCategory
  ]);

  const totalMatches=useMemo(()=>{
    return fixtures.reduce(
      (total,item)=>
        total+item.matches.length,
      0
    );
  },[fixtures]);

  const handleTournamentChange=(event)=>{
    const slug=event.target.value;

    setSelectedTournament(slug);
    setSelectedCategory("");
    setCategories([]);
    setPhases([]);
    setFixtures([]);
  };

  const handleCategoryChange=(_,slug)=>{
    setSelectedCategory(slug);
    setPhases([]);
    setFixtures([]);
  };

  return(
    <Box
      sx={{
        width:"100%",
        maxWidth:1300,
        mx:"auto"
      }}
    >
      <Box
        sx={{
          mb:2,
          display:"flex",
          flexDirection:{
            xs:"column",
            md:"row"
          },
          alignItems:{
            xs:"stretch",
            md:"flex-end"
          },
          justifyContent:"space-between",
          gap:1.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{
                xs:typography.sizes.xl,
                md:
                  typography.sizes.pageTitle||
                  typography.sizes.title
              },
              fontWeight:700,
              color:colors.text
            }}
          >
            Calendario de partidos
          </Typography>

          <Typography
            sx={{
              mt:0.35,
              fontSize:typography.sizes.md,
              color:colors.textSecondary
            }}
          >
            {totalMatches>0
              ?`${totalMatches} partido${totalMatches===1?"":"s"} en esta categoría`
              :"Consulta los partidos programados del torneo."
            }
          </Typography>
        </Box>

        <Box
          sx={{
            width:{
              xs:"100%",
              sm:330
            }
          }}
        >
          <Typography
            sx={{
              mb:0.5,
              fontSize:typography.sizes.sm,
              fontWeight:600,
              color:colors.textSecondary
            }}
          >
            Torneo
          </Typography>

          <FormControl
            fullWidth
            size="small"
          >
            <Select
              value={selectedTournament}
              onChange={handleTournamentChange}
              displayEmpty
              sx={{
                backgroundColor:colors.surface,
                borderRadius:
                  shape?.radiusSmall||
                  "8px",
                color:colors.text,
                "& .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.border
                },
                "&:hover .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.secondary
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.secondary
                }
              }}
            >
              {tournaments.length===0&&(
                <MenuItem
                  value=""
                  disabled
                >
                  No hay torneos disponibles
                </MenuItem>
              )}

              {tournaments.map(tournament=>(
                <MenuItem
                  key={tournament.slug}
                  value={tournament.slug}
                >
                  {tournament.name}
                  {tournament.season
                    ?` · ${tournament.season}`
                    :""
                  }
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {categories.length>1&&(
        <Box
          sx={{
            mb:2.5,
            borderBottom:
              `1px solid ${colors.border}`
          }}
        >
          <Tabs
            value={selectedCategory||false}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight:42,
              "& .MuiTabs-indicator":{
                height:3,
                backgroundColor:
                  colors.secondary
              },
              "& .MuiTab-root":{
                minHeight:42,
                px:2,
                py:0.8,
                textTransform:"none",
                fontSize:
                  typography.sizes.md,
                fontWeight:600,
                color:
                  colors.textSecondary
              },
              "& .Mui-selected":{
                color:
                  `${colors.text} !important`
              }
            }}
          >
            {categories.map(item=>{
              const slug=
                getCategorySlug(item);

              return(
                <Tab
                  key={slug}
                  value={slug}
                  label={
                    getCategoryName(item)
                  }
                />
              );
            })}
          </Tabs>
        </Box>
      )}

      {categories.length===1&&
        currentCategory&&(
          <Box
            sx={{
              mb:2.5,
              pb:1,
              borderBottom:
                `1px solid ${colors.border}`
            }}
          >
            <Typography
              sx={{
                fontSize:
                  typography.sizes.lg,
                fontWeight:700,
                color:colors.text
              }}
            >
              {getCategoryName(
                currentCategory
              )}
            </Typography>
          </Box>
        )
      }

      {loading?(
        <Box
          sx={{
            minHeight:220,
            display:"flex",
            alignItems:"center",
            justifyContent:"center"
          }}
        >
          <CircularProgress
            size={26}
            sx={{
              color:colors.secondary
            }}
          />
        </Box>
      ):!selectedTournament?(
        <EmptyState
          text="No hay un torneo seleccionado."
          colors={colors}
          typography={typography}
        />
      ):categories.length===0?(
        <EmptyState
          text="Este torneo no tiene categorías activas."
          colors={colors}
          typography={typography}
        />
      ):phases.length===0?(
        <EmptyState
          text="Esta categoría todavía no tiene fases configuradas."
          colors={colors}
          typography={typography}
        />
      ):totalMatches===0?(
        <EmptyState
          text="Todavía no existen partidos generados para esta categoría."
          colors={colors}
          typography={typography}
        />
      ):(
        <Box
          sx={{
            display:"flex",
            flexDirection:"column",
            gap:3
          }}
        >
          {fixtures.map(item=>{
            if(item.matches.length===0){
              return null;
            }

            return(
              <PhaseCalendar
                key={item.phase.slug}
                phase={item.phase}
                matches={item.matches}
                colors={colors}
                typography={typography}
                shape={shape}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

function PhaseCalendar({
  phase,
  matches,
  colors,
  typography,
  shape
}){
  const groupedMatches=useMemo(()=>{
    const result=[];

    matches.forEach(match=>{
      const groupSlug=
        match.group?.slug||
        match.group_slug||
        "";

      const groupName=
        match.group?.name||
        match.group_name||
        "";

      const roundOrder=
        match.round?.order??
        match.round_order??
        0;

      const roundName=
        match.round?.name||
        match.round_name||
        "Jornada";

      const leg=
        match.round?.leg??
        match.leg??
        1;

      let section=result.find(item=>
        item.groupSlug===groupSlug&&
        item.roundOrder===roundOrder&&
        item.leg===leg
      );

      if(!section){
        section={
          key:
            `${groupSlug||"phase"}-${roundOrder}-${leg}`,
          groupSlug,
          groupName,
          roundOrder,
          roundName,
          leg,
          matches:[]
        };

        result.push(section);
      }

      section.matches.push(match);
    });

    return result.sort((a,b)=>{
      if(a.groupName!==b.groupName){
        return a.groupName.localeCompare(
          b.groupName,
          "es",
          {
            sensitivity:"base",
            numeric:true
          }
        );
      }

      if(
        a.roundOrder!==
        b.roundOrder
      ){
        return(
          a.roundOrder-
          b.roundOrder
        );
      }

      return a.leg-b.leg;
    });
  },[matches]);

  return(
    <Box>
      <Box
        sx={{
          mb:1.5,
          display:"flex",
          alignItems:{
            xs:"flex-start",
            sm:"flex-end"
          },
          justifyContent:"space-between",
          flexDirection:{
            xs:"column",
            sm:"row"
          },
          gap:0.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:
                typography.sizes.xl,
              fontWeight:700,
              color:colors.text
            }}
          >
            {phase.name}
          </Typography>

          <Typography
            sx={{
              mt:0.15,
              fontSize:
                typography.sizes.sm,
              color:
                colors.textSecondary
            }}
          >
            {formatPhase(phase.format)}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize:
              typography.sizes.sm,
            color:
              colors.textSecondary
          }}
        >
          {matches.length} partido
          {matches.length===1?"":"s"}
        </Typography>
      </Box>

      <Box
        sx={{
          display:"flex",
          flexDirection:"column",
          gap:2
        }}
      >
        {groupedMatches.map(section=>(
          <Box key={section.key}>
            <Box
              sx={{
                mb:0.7,
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                gap:1
              }}
            >
              <Typography
                sx={{
                  fontSize:
                    typography.sizes.md,
                  fontWeight:700,
                  color:colors.text
                }}
              >
                {section.groupName
                  ?`${section.groupName} · ${section.roundName}`
                  :section.roundName
                }
              </Typography>

              {phase.round_trip&&(
                <Typography
                  sx={{
                    fontSize:
                      typography.sizes.sm,
                    color:
                      colors.textSecondary
                  }}
                >
                  {section.leg===2
                    ?"Vuelta"
                    :"Ida"
                  }
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                border:
                  `1px solid ${colors.border}`,
                borderRadius:
                  shape?.radius||
                  "10px",
                backgroundColor:
                  colors.surface,
                overflow:"hidden"
              }}
            >
              {section.matches.map(
                (match,index)=>(
                  <Box
                    key={
                      match.code||
                      `${section.key}-${index}`
                    }
                  >
                    {index>0&&(
                      <Divider
                        sx={{
                          borderColor:
                            colors.border
                        }}
                      />
                    )}

                    <MatchRow
                      match={match}
                      colors={colors}
                      typography={typography}
                    />
                  </Box>
                )
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function MatchRow({
  match,
  colors,
  typography
}){
  const result=getMatchResult(match);

  return(
    <Box
      sx={{
        px:{
          xs:1.2,
          sm:2
        },
        py:1.5,
        display:"grid",
        gridTemplateColumns:{
          xs:"1fr",
          md:
            "155px minmax(180px,1fr) 80px minmax(180px,1fr) 170px"
        },
        alignItems:"center",
        gap:{
          xs:1.2,
          md:1.5
        },
        transition:
          "background-color .15s ease",
        "&:hover":{
          backgroundColor:
            colors.backgroundSoft
        }
      }}
    >
      <Box
        sx={{
          display:"flex",
          flexDirection:{
            xs:"row",
            md:"column"
          },
          justifyContent:{
            xs:"center",
            md:"flex-start"
          },
          alignItems:{
            xs:"center",
            md:"flex-start"
          },
          gap:{
            xs:1,
            md:0.15
          }
        }}
      >
        <Typography
          sx={{
            fontSize:
              typography.sizes.sm,
            fontWeight:600,
            color:colors.text
          }}
        >
          {formatDate(match.date)}
        </Typography>

        <Typography
          sx={{
            fontSize:
              typography.sizes.sm,
            color:
              colors.textSecondary
          }}
        >
          {formatTime(
            match.start_time
          )}
        </Typography>
      </Box>

      <Team
        team={match.local}
        align="right"
        colors={colors}
        typography={typography}
      />

      <Box
        sx={{
          display:"flex",
          justifyContent:"center",
          alignItems:"center"
        }}
      >
        {result.finished?(
          <Box
            sx={{
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              gap:0.8,
              minWidth:70
            }}
          >
            <Typography
              sx={{
                fontSize:
                  typography.sizes.xl,
                fontWeight:700,
                color:colors.text
              }}
            >
              {result.local}
            </Typography>

            <Typography
              sx={{
                fontSize:
                  typography.sizes.sm,
                color:
                  colors.textSecondary
              }}
            >
              -
            </Typography>

            <Typography
              sx={{
                fontSize:
                  typography.sizes.xl,
                fontWeight:700,
                color:colors.text
              }}
            >
              {result.visitor}
            </Typography>
          </Box>
        ):(
          <Typography
            sx={{
              fontSize:
                typography.sizes.sm,
              fontWeight:700,
              color:
                colors.textSecondary
            }}
          >
            VS
          </Typography>
        )}
      </Box>

      <Team
        team={match.visitor}
        align="left"
        colors={colors}
        typography={typography}
      />

      <Box
        sx={{
          display:"flex",
          flexDirection:"column",
          alignItems:{
            xs:"center",
            md:"flex-end"
          },
          gap:0.15
        }}
      >
        <Typography
          sx={{
            maxWidth:170,
            textAlign:{
              xs:"center",
              md:"right"
            },
            fontSize:
              typography.sizes.sm,
            fontWeight:600,
            color:colors.text
          }}
        >
          {getVenueName(match)}
        </Typography>

        <Typography
          sx={{
            fontSize:
              typography.sizes.xs||
              typography.sizes.sm,
            color:
              colors.textSecondary
          }}
        >
          {match.status_display||
            (match.status==="SCHEDULED"
              ?"Programado"
              :match.status==="LIVE"
                ?"En vivo"
                :match.status==="FINISHED"
                  ?"Finalizado"
                  :match.status||
                    ""
            )
          }
        </Typography>
      </Box>
    </Box>
  );
}

function Team({
  team,
  align,
  colors,
  typography
}){
  const logo=getTeamLogo(team);
  const reverse=align==="right";

  return(
    <Box
      sx={{
        display:"flex",
        alignItems:"center",
        justifyContent:{
          xs:"center",
          md:
            reverse
              ?"flex-end"
              :"flex-start"
        },
        flexDirection:
          reverse
            ?{
                xs:"row",
                md:"row"
              }
            :"row",
        gap:1
      }}
    >
      {reverse?(
        <>
          <Typography
            sx={{
              textAlign:{
                xs:"center",
                md:"right"
              },
              fontSize:
                typography.sizes.md,
              fontWeight:700,
              color:colors.text,
              lineHeight:1.3
            }}
          >
            {getTeamName(team)}
          </Typography>

          {logo&&(
            <Box
              component="img"
              src={logo}
              alt={getTeamName(team)}
              sx={{
                width:34,
                height:34,
                objectFit:"contain",
                flexShrink:0
              }}
            />
          )}
        </>
      ):(
        <>
          {logo&&(
            <Box
              component="img"
              src={logo}
              alt={getTeamName(team)}
              sx={{
                width:34,
                height:34,
                objectFit:"contain",
                flexShrink:0
              }}
            />
          )}

          <Typography
            sx={{
              textAlign:{
                xs:"center",
                md:"left"
              },
              fontSize:
                typography.sizes.md,
              fontWeight:700,
              color:colors.text,
              lineHeight:1.3
            }}
          >
            {getTeamName(team)}
          </Typography>
        </>
      )}
    </Box>
  );
}

function EmptyState({
  text,
  colors,
  typography
}){
  return(
    <Box
      sx={{
        py:5,
        px:2,
        borderTop:
          `1px solid ${colors.border}`,
        borderBottom:
          `1px solid ${colors.border}`,
        textAlign:"center"
      }}
    >
      <Typography
        sx={{
          fontSize:
            typography.sizes.md,
          color:
            colors.textSecondary
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}